import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import StoryCard from '@/components/StoryCard';
import SEOHead from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Sparkles, Loader2 } from 'lucide-react';

/**
 * Public author profile page.
 * URL: /autor/:id
 *
 * Minimal-but-shipping-safe implementation — shows author name/avatar/bio (when
 * available) and their list of published community stories. Preserves the
 * Community-Author routing so the Browse "por [Author]" links always land
 * somewhere real.
 */
export default function AuthorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/authors/${id}`);
        if (!cancelled) setAuthor(data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.detail || 'Autor não encontrado');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ruby" />
      </div>
    );
  }

  if (error || !author) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-24 pb-12 px-4 md:px-12 max-w-3xl mx-auto text-center">
          <User className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-brand text-2xl font-bold text-foreground mb-2">Autor não encontrado</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/browse')} className="bg-ruby hover:bg-ruby-light">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar aos contos
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const initial = (author.name || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={`${author.name} · Contos na Intimate`} description={`Contos publicados por ${author.name} na Intimate.pt.`} type="profile" />
      <Navbar />

      <main className="pt-20 md:pt-24 pb-20 px-4 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-muted-foreground hover:text-foreground"
            data-testid="author-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          {/* Author header */}
          <div className="flex flex-col sm:flex-row items-start gap-5 mb-10 p-6 rounded-2xl bg-surface border border-border">
            {author.avatar_url ? (
              <img
                src={author.avatar_url}
                alt={author.name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-ruby/40"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-ruby to-ruby/50 flex items-center justify-center border-2 border-ruby/40">
                <span className="font-brand text-3xl font-bold text-white">{initial}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h1 className="font-brand text-2xl md:text-3xl font-bold text-foreground" data-testid="author-name">
                  {author.name}
                </h1>
                {author.is_official && (
                  <Badge className="bg-gold text-black text-xs uppercase tracking-wider">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Intimate Oficial
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                {author.story_count} {author.story_count === 1 ? 'conto publicado' : 'contos publicados'}
              </p>
              {author.bio ? (
                <p className="text-sm md:text-base text-foreground/85 whitespace-pre-wrap max-w-2xl">
                  {author.bio}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground italic max-w-2xl">
                  A página completa do autor está em construção. Em breve encontrarás aqui uma biografia, redes sociais e mais.
                </p>
              )}
            </div>
          </div>

          {/* Stories grid */}
          <h2 className="font-brand text-xl md:text-2xl font-semibold text-foreground mb-4">
            Contos deste autor
          </h2>
          {author.stories.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
              {author.stories.map((s) => (
                <StoryCard key={s.id} story={s} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6">
              Este autor ainda não tem contos publicados.
            </p>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
